/**
 * Resolve a block image field to a src.
 *
 * Branding used to come from fixed content ids at the site root
 * (site-thumbnail, site-header-banner, site-footer-banner) written by the Data
 * Manager Admin block. That was one set of images per Plone SITE, so every
 * search page in a site necessarily shared them - unworkable once a site hosts
 * a search page per agency. That block has been removed; images are now
 * ordinary object_browser values on the search and details blocks, so each page
 * carries its own.
 *
 * Returns undefined when nothing is set, so the caller can skip rendering the
 * <img> rather than emitting one with no source.
 */
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

export interface BlockImageItem {
  '@id'?: string;
  image_field?: string;
  image_scales?: Record<string, Array<{ base_path?: string; download?: string }>>;
}

export const resolveBlockImageSrc = (
  items: BlockImageItem[] | undefined,
): string | undefined => {
  const item = Array.isArray(items) ? items[0] : undefined;

  if (!item?.['@id']) {
    return undefined;
  }

  const field = item.image_field || 'image';

  // Use the resolved scale from image_scales, as Volto's own Image component
  // does (components/theme/Image/Image.jsx). `download` is a concrete scale -
  // '@@images/image-1200-abc.png' - so the browser fetches it directly. Asking
  // for '@@images/<field>' instead makes Plone 302 to that URL on the CMS host,
  // a cross-origin image the CSP then blocks.
  const scale = item.image_scales?.[field]?.[0];
  const basePath = scale?.base_path || item['@id'];
  const path = flattenToAppURL(basePath);

  // ++api++ routes through Volto's proxy to Plone.
  return scale?.download
    ? `/++api++${path}/${scale.download}`
    : `/++api++${path}/@@images/${field}`;
};
